import React, {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useI18nContext } from "@hopper-b2b/i18n";
import { IconComponent, IconName } from "@hopper-b2b/ui";
import {
  Box,
  Divider,
  ListItem,
  ListSubheader,
  Popper,
  type PopperProps,
  TextField,
} from "@material-ui/core";
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
} from "@material-ui/lab";
import clsx from "clsx";

import "./styles.scss";
import type { Suggestion } from "@b2bportal/air-shopping-api";
import { isIOSDevice } from "../../../utils/deviceDetection";
import { useNubankFeatureFlagsContext } from "../../../utils/featureFlagsContext";

export type CategoryType<ValueType> = {
  results: ValueType[];
  label: string;
  category: string;
};

export type OptionType<ValueType> = ValueType & {
  categoryLabel: string;
};

export interface IB2BSearchAutocompleteProps<ValueType> {
  value: ValueType | null;
  valueCategories: CategoryType<ValueType>[] | null;
  setValue: (value: ValueType | null) => void;
  fetchValueCategories: (queryString: string) => void;
  isFocusedAutocomplete: boolean;
  setFocusedAutocomplete: () => void;
  clearFocusedAutocomplete: () => void;
  loading?: boolean;
  loadingText?: string;
  className?: string;
  hideIcon?: boolean;
  autoFocus?: boolean;
  openOnFocus?: boolean;
  keepOpen?: boolean;
  icon?: IconProp;
  customIcon?: JSX.Element;
  topContent?: JSX.Element;
  afterSetValue?: () => void;
  afterSetValidValue?: () => void;
  initialInputValue?: string;
  overrideInputValue?: string;
  getOptionSelected: (
    option: OptionType<ValueType>,
    value: ValueType
  ) => boolean;
  hideFloatingLabel?: boolean;
  label: string;
  popperClassName?: string;
  disabled?: boolean;
  noOptionsText?: ReactNode;
  isComboBox?: boolean;
  onBlur?: () => void;
  onClose?: () => void;
  onFocus?: () => void;
  onClick?: () => void;
  popupIcon?: JSX.Element;
  noTabIndex?: boolean;
  disablePortal?: boolean;
  freeSolo?: boolean;
}

export const AccessibleAutocomplete = <
  ValueType extends {
    label: string;
  },
>(
  props: IB2BSearchAutocompleteProps<ValueType>
): JSX.Element => {
  const {
    loading,
    loadingText,
    className,
    value,
    valueCategories,
    hideIcon,
    autoFocus,
    openOnFocus,
    icon,
    customIcon,
    topContent,
    setValue,
    afterSetValue,
    afterSetValidValue,
    getOptionSelected,
    fetchValueCategories,
    initialInputValue,
    overrideInputValue,
    hideFloatingLabel,
    label,
    popperClassName,
    disabled,
    noOptionsText,
    isComboBox,
    onBlur,
    onClose,
    onFocus,
    popupIcon,
    onClick,
    noTabIndex,
    disablePortal,
    freeSolo,
    isFocusedAutocomplete,
    setFocusedAutocomplete,
    clearFocusedAutocomplete,
  } = props;

  const { t } = useI18nContext();
  const [openAutocomplete, setOpenAutocomplete] = useState<boolean>(false);

  const getOptionLabel = (option: OptionType<ValueType>) => option.label;

  const AutocompleteCustomPopper = (props: PopperProps) => {
    return (
      <Popper
        disablePortal={disablePortal}
        {...props}
        className={clsx(
          "nubank",
          "autocomplete-popper",
          { focused: isFocusedAutocomplete },
          popperClassName
        )}
        placement={
          popperClassName === "mobile" ? "right-start" : "bottom-start"
        }
      />
    );
  };

  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState(
    value ? value.label : initialInputValue || ""
  );

  const handleSelection = useCallback(
    (_: React.ChangeEvent<unknown>, value: ValueType | null): void => {
      if (!disabled) {
        setOpenAutocomplete(false);
        setValue(value);
        clearFocusedAutocomplete();
        afterSetValue && afterSetValue();
        value?.label && afterSetValidValue && afterSetValidValue();
      }
    },
    [
      afterSetValidValue,
      afterSetValue,
      clearFocusedAutocomplete,
      disabled,
      setValue,
    ]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<unknown>, inputValue: string) => {
      if (!disabled) {
        // Only fetch for genuine queries: trimmed, non-empty. The input
        // state itself still updates on every keystroke.
        const query = inputValue.trim();
        if (query.length > 0 && e && e.type === "change") {
          fetchValueCategories(query);
        }

        if (e) setInputValue(inputValue);
      }
    },
    [disabled, fetchValueCategories]
  );

  const handleClear = useCallback(() => {
    if (!disabled) {
      setInputValue("");
      setValue(null);
      afterSetValue && afterSetValue();
    }
  }, [afterSetValue, disabled, setValue]);

  const options = useMemo(() => {
    if (valueCategories?.length > 0) {
      return valueCategories.flatMap((category) => {
        const { results, label: categoryLabel } = category;

        return results.map((result) => ({
          ...result,
          categoryLabel,
        }));
      });
    }

    return [];
  }, [valueCategories]);

  // Updates the inputValue when value (e.g.: origin / destination) is parsed and set in the consumer
  useEffect(() => {
    if (overrideInputValue) setInputValue(overrideInputValue);
    else if (value) {
      setInputValue(value.label);
    } else {
      setInputValue("");
    }
  }, [value, overrideInputValue]);

  const chosenOption = value
    ? (options.find((option) => getOptionSelected(option, value)) ?? value)
    : null;

  const renderGroup = useCallback(
    (params: any) => {
      const renderTopContent = () => {
        if (topContent && params.key === 0) {
          return (
            <>
              <ListItem>{topContent}</ListItem>
              <Divider />
            </>
          );
        }
        return null;
      };
      return [
        <div key={params.group}>
          {renderTopContent()}
          <ListSubheader key={params.key} component="div" tabIndex={-1}>
            {params.group}
          </ListSubheader>
        </div>,
        params.children.map((e) => ({
          ...e,
          props: { ...e.props, tabIndex: 2 },
        })),
      ];
    },
    [topContent]
  );

  const autoCompleteInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      return (
        <B2BAutoCompleteInput
          {...{
            ...params,
            hideIcon,
            autoFocus,
            icon,
            customIcon,
            label,
            hideFloatingLabel,
            disabled: !!disabled,
            ariaExpanded:
              (!disabled &&
                !overrideInputValue &&
                inputValue.length > 0 &&
                (!value || value?.label !== inputValue)) ||
              (options.length > 1 && focused) ||
              (!!overrideInputValue && inputValue.length > 0 && focused),
            handleClear: handleClear,
            noTabIndex,
          }}
        />
      );
    },
    [
      autoFocus,
      customIcon,
      disabled,
      focused,
      handleClear,
      hideFloatingLabel,
      hideIcon,
      icon,
      inputValue,
      label,
      noTabIndex,
      options.length,
      overrideInputValue,
      value,
    ]
  );

  const onBlurAutocomplete = useCallback(() => {
    if (onBlur) onBlur();
  }, [onBlur]);

  const onCloseAutoComplete = useCallback(() => {
    onClose && onClose();
    if (!disabled) setFocused(false);
  }, [disabled, onClose]);

  const onFocusAutocomplete = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      if (!disabled) {
        setOpenAutocomplete(true);
        setFocused(true);
        setFocusedAutocomplete();

        // A selected suggestion keeps its full display label in the input on
        // refocus; select it so the next keystroke replaces it instead of
        // appending (which used to fire mangled queries like
        // "São Paulo, Brasil (5 aeroportos)rio"). Skipped when the user is
        // mid-edit (input no longer matches the selected label).
        const input = e.target as HTMLInputElement;
        if (
          value &&
          input?.tagName === "INPUT" &&
          input.value === value.label
        ) {
          input.select();
        }
      }
      if (onFocus) onFocus();
    },
    [disabled, onFocus, setFocusedAutocomplete, value]
  );

  const renderOption = (option: Suggestion) => {
    const commaPosition = option.label.indexOf(",");
    const firstSlice = option.label.slice(0, commaPosition);
    return (
      <>
        <b>{firstSlice}</b>
        {option.label.slice(commaPosition)}
      </>
    );
  };

  // iOS jitter fix: intercept touchstart on inputs, prevent default,
  // then manually focus with preventScroll:true. This stops iOS from
  // scrolling the viewport when an input receives focus or when a
  // search result is selected and focus moves to the next field.
  // Only applied on iOS — on Android, preventDefault() on touchstart
  // suppresses the click event chain that MUI Autocomplete needs.
  // See NubankSimpleModal/IOS_KEYBOARD_FIX.md for details.
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const { disableTouchFix } = useNubankFeatureFlagsContext();
  useEffect(() => {
    if (disableTouchFix || !isIOSDevice()) return;

    const root = searchBoxRef.current;
    if (!root) return;

    const onTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT"
      ) {
        e.preventDefault();
        target.focus({ preventScroll: true });
      }
    };

    root.addEventListener("touchstart", onTouchStart, { passive: false });
    return () => root.removeEventListener("touchstart", onTouchStart);
  }, [disableTouchFix]);

  return (
    <Box ref={searchBoxRef} className={clsx("terminus-search", className)}>
      <Autocomplete
        autoComplete
        clearOnBlur={false}
        disableClearable
        value={chosenOption}
        inputValue={inputValue}
        onChange={handleSelection}
        open={openAutocomplete}
        popupIcon={popupIcon}
        PopperComponent={AutocompleteCustomPopper}
        onInputChange={handleInputChange}
        onBlur={onBlurAutocomplete}
        onFocus={onFocusAutocomplete}
        onClose={onCloseAutoComplete}
        openOnFocus={openOnFocus}
        filterOptions={(options) => options}
        className="terminus-search"
        loading={loading}
        loadingText={loadingText || ""}
        options={isFocusedAutocomplete ? options : []}
        groupBy={(option: OptionType<ValueType>) => option.categoryLabel}
        getOptionLabel={getOptionLabel}
        freeSolo={freeSolo}
        renderGroup={renderGroup}
        renderInput={autoCompleteInput}
        renderOption={renderOption}
        disabled={disabled}
        noOptionsText={isFocusedAutocomplete ? noOptionsText : ""}
        onClick={onClick}
        // Accessibility Props
        role={isComboBox ? "combobox" : undefined}
        aria-expanded={undefined}
        aria-label={label}
        aria-autocomplete="list"
        aria-owns="options"
      />
      <span id="initInstr" style={{ display: "none" }}>
        {t("autocompleteAriaHelperText")}
      </span>
      <div aria-live="assertive" className="screen-reader-text"></div>
    </Box>
  );
};

export interface IB2BAutoCompleteInputProps
  extends AutocompleteRenderInputParams {
  autoFocus?: boolean;
  hideIcon?: boolean;
  icon?: IconProp;
  customIcon?: JSX.Element;
  hideFloatingLabel?: boolean;
  label: string;
  ariaExpanded?: boolean;
  handleClear: () => void;
  noTabIndex?: boolean;
}

const B2BAutoCompleteInput = (props: IB2BAutoCompleteInputProps) => {
  const {
    InputProps: {
      endAdornment = null,
      startAdornment = null,
      ...defaultInputProps
    },
    autoFocus,
    hideIcon,
    icon,
    customIcon,
    hideFloatingLabel,
    label,
    ariaExpanded,
    handleClear,
    noTabIndex,
    ...defaultProps
  } = props;

  const [focused, setFocused] = useState<boolean>(false);
  const onFocus = useCallback(
    () => !defaultProps.disabled && setFocused(true),
    [defaultProps.disabled]
  );
  const onBlur = useCallback(
    () => !defaultProps.disabled && setFocused(false),
    [defaultProps.disabled]
  );
  const {
    inputProps: { value },
  }: { inputProps: any } = useMemo(() => defaultProps, [defaultProps]);

  defaultProps.inputProps = {
    ...defaultProps.inputProps,
    role: "combobox",
    "aria-autocomplete": "list",
    "aria-expanded": ariaExpanded,
    "aria-haspopup": true,
    "aria-describedby": "initInstr",
  };

  return (
    <Box
      className={`b2b-autocomplete-input ${focused && "focused"}`}
      tabIndex={noTabIndex ? undefined : 0}
    >
      {!hideIcon && (
        <Box className={clsx("b2b-autocomplete-input-icon-container")}>
          {customIcon ?? (
            <FontAwesomeIcon
              className="icon"
              icon={icon ?? (faMapMarkerAlt as IconProp)}
            />
          )}
        </Box>
      )}
      <TextField
        {...defaultProps}
        fullWidth={true}
        name="terminus-input"
        label={hideFloatingLabel ? null : label}
        autoFocus={autoFocus}
        InputProps={{
          endAdornment,
          startAdornment,
          disableUnderline: true,
        }}
        value={value || ""}
        {...defaultInputProps}
        className={clsx("b2b-autocomplete-input-text")}
        onFocus={onFocus}
        onBlur={onBlur}
        tabIndex={1}
      />
      <button
        className={clsx("b2b-autocomplete-input-clear-button")}
        onClick={() => handleClear()}
        type={"button"}
      >
        <IconComponent
          ariaLabel="Close button icon"
          className={clsx("close-button-icon")}
          name={IconName.Close}
        />
      </button>
    </Box>
  );
};
